"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Plus, X } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { formatCurrency } from "@/lib/utils";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { STATUS_IDS } from "@/lib/constants/statuses";
import { AccountingArticleMapping } from "@/components/admin/shared/accounting-article-mapping";
import { ItemFaqsTab } from "@/components/admin/faqs/item-faqs-tab";

// Form validation schema
const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  category_id: z.number().min(1, "Category is required"),
  image_url: z.string().nullable(),
  status_id: z.number(),
});

type FormData = z.infer<typeof formSchema>;

interface PriceRecord {
  id: number;
  price: number;
  vat_percentage: number;
  currency: string;
  status: string;
  created_at: string;
}

interface ShopItem {
  id: number;
  venue_id: number;
  name: string;
  category_id: number;
  pricing_id: number | null;
  image_url: string | null;
  status_id: number;
  article_code?: string | null;
  created_at: string;
  updated_at: string;
  category: {
    id: number;
    name: string;
  } | null;
  pricing: {
    id: number;
    price: number;
    vat_percentage: number;
    currency: string;
  } | null;
}

interface EditShopItemFormProps {
  venueId: number;
  shopItem: ShopItem;
  venueName: string;
  venueCurrency: string;
  venueAccountingId?: number | null;
  userRoleId?: number;
}

export function EditShopItemForm({
  venueId,
  shopItem,
  venueName,
  venueCurrency,
  venueAccountingId,
  userRoleId,
}: EditShopItemFormProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"general" | "pricing" | "faqs">(
    "general",
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Categories state
  const [categories, setCategories] = useState<
    Array<{ id: number; name: string }>
  >([]);

  // Pricing state
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [newPriceData, setNewPriceData] = useState({
    price: "",
    vatPercentage: "25",
  });
  const [isAddingPrice, setIsAddingPrice] = useState(false);
  const [priceHistory, setPriceHistory] = useState<PriceRecord[]>([]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: shopItem.name,
      category_id: shopItem.category_id,
      image_url: shopItem.image_url,
      status_id: shopItem.status_id || STATUS_IDS.SHOP_ITEM_ACTIVE,
    },
  });

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsDataLoading(true);
        let hasError = false;

        // Load categories
        try {
          const categoriesResponse = await fetch(
            `/api/venues/${venueId}/shop-item-categories`,
          );
          if (categoriesResponse.ok) {
            const categoriesData = await categoriesResponse.json();
            setCategories(categoriesData || []);
          } else {
            console.error(
              "Failed to fetch categories:",
              categoriesResponse.status,
            );
            hasError = true;
          }
        } catch (error) {
          console.error("Error fetching categories:", error);
          hasError = true;
        }

        // Load pricing history
        try {
          const pricingHistoryResponse = await fetch(
            `/api/venues/${venueId}/shop-items/${shopItem.id}/pricing-history`,
          );
          if (pricingHistoryResponse.ok) {
            const result = await pricingHistoryResponse.json();
            setPriceHistory(result.data || []);
          } else {
            console.error(
              "Failed to fetch pricing history:",
              pricingHistoryResponse.status,
            );
            hasError = true;
          }
        } catch (error) {
          console.error("Error fetching pricing history:", error);
          hasError = true;
        }

        if (hasError) {
          toast.error(
            "Some data could not be loaded. Please refresh the page.",
          );
        }
      } catch (error) {
        console.error("Error loading shop item data:", error);
        toast.error("Failed to load shop item details. Please try again.");
      } finally {
        setIsDataLoading(false);
      }
    };

    loadInitialData();
  }, [venueId, shopItem.id]);

  const handleGoBack = () => {
    const currentPortal = pathname.startsWith("/booking")
      ? "booking"
      : "superadmin";
    router.push(`/${currentPortal}/venue/${venueId}/shop-items`);
  };

  const handleAddNewPrice = async () => {
    if (!newPriceData.price) return;

    setIsAddingPrice(true);
    try {
      // First, archive all currently active prices for this shop item
      const archiveResponse = await fetch(
        `/api/venues/${venueId}/shop-items/${shopItem.id}/archive-prices`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!archiveResponse.ok) {
        throw new Error("Failed to archive current prices");
      }

      // Then, create a new pricing record
      const createPricingResponse = await fetch("/api/pricings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          price: parseFloat(newPriceData.price),
          vat_percentage: parseFloat(newPriceData.vatPercentage),
          price_category: "shopitem",
          item_id: shopItem.id,
          status_id: 41, // PRICING_ACTIVE
          currency: venueCurrency,
        }),
      });

      if (!createPricingResponse.ok) {
        const error = await createPricingResponse.json();
        throw new Error(error.error || "Failed to create new pricing");
      }

      const newPricing = await createPricingResponse.json();

      // Finally, update the shop item to use the new pricing
      const response = await fetch(
        `/api/venues/${venueId}/shop-items/${shopItem.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: form.getValues("name") || shopItem.name,
            category_id: form.getValues("category_id") || shopItem.category_id,
            pricing_id: newPricing.id,
            image_url: form.getValues("image_url"),
            status_id: form.getValues("status_id"),
          }),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update shop item pricing");
      }

      // Refresh pricing history
      const pricingHistoryResponse = await fetch(
        `/api/venues/${venueId}/shop-items/${shopItem.id}/pricing-history`,
      );
      if (pricingHistoryResponse.ok) {
        const { data } = await pricingHistoryResponse.json();
        setPriceHistory(data || []);
      }

      setPriceModalOpen(false);
      setNewPriceData({ price: "", vatPercentage: "25" });

      // Show success message
      toast.success("Price updated successfully");

      // Refresh the page to ensure data consistency
      router.refresh();
    } catch (error: any) {
      toast.error(error.message || "Failed to update pricing");
    } finally {
      setIsAddingPrice(false);
    }
  };

  // Show loading spinner while fetching initial data
  if (isDataLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-purple-600" />
          <p className="text-gray-600 font-medium">
            Loading shop item details...
          </p>
        </div>
      </div>
    );
  }

  const onSubmit = async (values: FormData) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `/api/venues/${venueId}/shop-items/${shopItem.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: values.name,
            category_id: values.category_id,
            image_url: values.image_url,
            status_id: values.status_id,
            pricing_id: shopItem.pricing_id, // Keep current pricing
          }),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update shop item");
      }

      toast.success("Shop item updated successfully");
      router.refresh();
    } catch (error: any) {
      toast.error(error.message || "Failed to update shop item");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto py-6">
        <div className="max-w-7xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={handleGoBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-3xl font-bold">Edit shop item: {shopItem.name}</h1>
                <p className="text-gray-600 mt-1">
                  Manage shop item details and pricing for {venueName}
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg border">
          <div className="border-b border-gray-200">
            <nav className="px-6 -mb-px flex space-x-8" aria-label="Tabs">
              <button
                type="button"
                onClick={() => setActiveTab("general")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "general"
                    ? "border-purple-600 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                General settings
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("pricing")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "pricing"
                    ? "border-purple-600 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Pricing
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("faqs")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "faqs"
                    ? "border-purple-600 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                FAQs
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                {/* General Tab */}
                {activeTab === "general" && (
                  <div className="space-y-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Name <span className="text-destructive">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Cola, T-Shirt, etc."
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="category_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Category <span className="text-destructive">*</span>
                          </FormLabel>
                          <Select
                            onValueChange={(value) =>
                              field.onChange(parseInt(value))
                            }
                            defaultValue={field.value?.toString()}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {categories.map((category) => (
                                <SelectItem
                                  key={category.id}
                                  value={category.id.toString()}
                                >
                                  {category.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="image_url"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Image</FormLabel>
                          <div className="space-y-4">
                            {field.value && (
                              <div className="relative inline-block">
                                <img
                                  src={field.value}
                                  alt="Shop item preview"
                                  className="h-32 w-32 object-contain rounded-lg border bg-gray-50"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    field.onChange(null);
                                    if (fileInputRef.current) {
                                      fileInputRef.current.value = "";
                                    }
                                  }}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <Input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    const formData = new FormData();
                                    formData.append("file", file);
                                    try {
                                      const response = await fetch(
                                        "/api/upload",
                                        {
                                          method: "POST",
                                          body: formData,
                                        },
                                      );
                                      if (response.ok) {
                                        const { url } = await response.json();
                                        field.onChange(url);
                                      } else {
                                        const errorData = await response
                                          .json()
                                          .catch(() => ({
                                            error: "Failed to upload image",
                                          }));
                                        toast.error(
                                          errorData.error ||
                                            "Failed to upload image",
                                        );
                                      }
                                    } catch (error) {
                                      toast.error(
                                        error instanceof Error
                                          ? error.message
                                          : "Failed to upload image",
                                      );
                                    }
                                  }
                                }}
                                className="flex-1"
                              />
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Recommended 4:3 (e.g. 1200x900). Images are
                              center-cropped in the UI.
                            </p>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="status_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select
                            onValueChange={(value) =>
                              field.onChange(parseInt(value))
                            }
                            defaultValue={field.value?.toString()}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem
                                value={STATUS_IDS.SHOP_ITEM_ACTIVE.toString()}
                              >
                                Active
                              </SelectItem>
                              <SelectItem
                                value={STATUS_IDS.SHOP_ITEM_ARCHIVED.toString()}
                              >
                                Archived
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Accounting Article Mapping */}
                    <AccountingArticleMapping
                      venueId={venueId}
                      itemId={shopItem.id}
                      itemType="shop-item"
                      itemName={shopItem.name}
                      currentArticleCode={shopItem.article_code || null}
                      accountingId={venueAccountingId}
                      userRoleId={userRoleId}
                    />

                    <div className="flex gap-4">
                      <Button type="submit" disabled={isLoading}>
                        {isLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          "Save changes"
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleGoBack}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Pricing Tab */}
                {activeTab === "pricing" && (
                  <div className="space-y-6">
                    {/* Price History Table */}
                    <div className="border rounded-lg p-6 space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold">Price history</h3>
                        <Button
                          type="button"
                          onClick={() => {
                            // Reset form data when opening modal
                            setNewPriceData({
                              price: "",
                              vatPercentage: "25",
                            });
                            setPriceModalOpen(true);
                          }}
                          className="bg-purple-600 hover:bg-purple-700"
                          size="sm"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add new price
                        </Button>
                      </div>

                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Price ({getCurrencySymbol(venueCurrency)})</TableHead>
                              <TableHead>VAT %</TableHead>
                              <TableHead>Total (incl. VAT)</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Created date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {priceHistory.length === 0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={5}
                                  className="text-center text-gray-500 py-8"
                                >
                                  No pricing history available
                                </TableCell>
                              </TableRow>
                            ) : (
                              priceHistory.map((record) => {
                                const total =
                                  record.price *
                                  (1 + record.vat_percentage / 100);
                                const getStatusBadgeClass = () => {
                                  if (
                                    !record.status ||
                                    typeof record.status !== "string"
                                  ) {
                                    return "bg-gray-100 text-gray-800 border-gray-200";
                                  }

                                  const statusLower =
                                    record.status.toLowerCase();

                                  switch (statusLower) {
                                    case "active":
                                      return "bg-green-100 text-green-800 border-green-200";
                                    case "inactive":
                                    case "archived":
                                      return "bg-gray-100 text-gray-800 border-gray-200";
                                    default:
                                      return "bg-gray-100 text-gray-800 border-gray-200";
                                  }
                                };

                                return (
                                  <TableRow key={record.id}>
                                    <TableCell className="font-medium">
                                      {formatCurrency(
                                        record.price,
                                        record.currency,
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {record.vat_percentage}%
                                    </TableCell>
                                    <TableCell className="font-medium">
                                      {formatCurrency(total, record.currency)}
                                    </TableCell>
                                    <TableCell>
                                      <Badge
                                        variant="outline"
                                        className={getStatusBadgeClass()}
                                      >
                                        {record.status || "Unknown"}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      {new Date(
                                        record.created_at,
                                      ).toLocaleDateString()}
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </Form>

            {/* FAQs Tab - outside the item's form, it saves on its own */}
            {activeTab === "faqs" && (
              <ItemFaqsTab
                kind="shop_item"
                itemId={shopItem.id}
                itemName={shopItem.name}
                venueId={venueId}
              />
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Add New Price Modal */}
      <Dialog open={priceModalOpen} onOpenChange={setPriceModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add new price</DialogTitle>
            <DialogDescription>
              Set a new price for this shop item. The new price will be active
              immediately.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-price">Price ({getCurrencySymbol(venueCurrency)}) *</Label>
                <Input
                  id="new-price"
                  type="number"
                  value={newPriceData.price}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value >= 0) {
                      setNewPriceData((prev) => ({
                        ...prev,
                        price: e.target.value,
                      }));
                    } else if (e.target.value === "") {
                      setNewPriceData((prev) => ({
                        ...prev,
                        price: "",
                      }));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e") {
                      e.preventDefault();
                    }
                  }}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-vat">VAT percentage (%)</Label>
                <Input
                  id="new-vat"
                  type="number"
                  value={newPriceData.vatPercentage}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value >= 0 && value <= 100) {
                      setNewPriceData((prev) => ({
                        ...prev,
                        vatPercentage: e.target.value,
                      }));
                    } else if (e.target.value === "") {
                      setNewPriceData((prev) => ({
                        ...prev,
                        vatPercentage: "",
                      }));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e") {
                      e.preventDefault();
                    }
                  }}
                  placeholder="25"
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
            </div>

            {/* Price Preview */}
            {newPriceData.price && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3">Price preview</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Base price:</span>
                    <span className="font-medium">
                      {newPriceData.price} {getCurrencySymbol(venueCurrency)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>VAT ({newPriceData.vatPercentage}%):</span>
                    <span className="font-medium">
                      +
                      {(
                        (parseFloat(newPriceData.price) *
                          parseFloat(newPriceData.vatPercentage)) /
                        100
                      ).toFixed(2)}{" "}
                      {getCurrencySymbol(venueCurrency)}
                    </span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between font-semibold">
                      <span>Total (incl. VAT):</span>
                      <span className="text-purple-600">
                        {(
                          parseFloat(newPriceData.price) *
                          (1 + parseFloat(newPriceData.vatPercentage) / 100)
                        ).toFixed(2)}{" "}
                        {getCurrencySymbol(venueCurrency)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddNewPrice}
              disabled={!newPriceData.price || isAddingPrice}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isAddingPrice ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add price"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
